/**
 * VariablesManagerModal.tsx
 * Modal pour gérer les variables dynamiques des textes
 * Permet d'ajouter, modifier et supprimer des variables {{key}}
 */
import React, { useState, useEffect } from 'react';
import { Modal } from './common/Modal';
import { ConfirmModal } from './common/ConfirmModal';
import { useAppCoordinatorOptional } from '../../hooks/useAppCoordinator';
import type { TextVariables } from '../../services/TextToolService';
import { Button, Input, IconButton, Card, Divider } from '../../ui';
import { Plus, Trash2, Edit } from 'lucide-react';

interface VariablesManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const VariablesManagerModal: React.FC<VariablesManagerModalProps> = ({
  isOpen,
  onClose,
}) => {
  const appCoordinator = useAppCoordinatorOptional();
  const [variables, setVariables] = useState<TextVariables>({});
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [editingKey, setEditingKey] = useState<string | null>(null);
  // A18 : clé de variable en attente de suppression (ouvre la ConfirmModal).
  const [deleteKey, setDeleteKey] = useState<string | null>(null);

  // Charger les variables depuis TextToolService
  useEffect(() => {
    if (!isOpen) return;

    if (appCoordinator) {
      const textToolService = appCoordinator.getTextToolService();
      if (textToolService) {
        const allVariables = textToolService.getAllVariables();
        setVariables(allVariables);
      }
    }
  }, [isOpen, appCoordinator]);

  // Ajouter une nouvelle variable
  const handleAddVariable = () => {
    if (!newKey.trim() || !newValue.trim()) {
      return;
    }

    // Valider le format de la clé (pas de caractères spéciaux)
    if (!/^[a-zA-Z0-9_]+$/.test(newKey.trim())) {
      // Afficher un message d'erreur visuel
      const errorMsg = document.createElement('div');
      errorMsg.textContent = 'La clé ne peut contenir que des lettres, chiffres et underscores';
      errorMsg.style.cssText =
        'position: fixed; top: 20px; right: 20px; background: #ef4444; color: white; padding: 12px 16px; border-radius: 6px; z-index: 10000; box-shadow: 0 4px 6px rgba(0,0,0,0.1);';
      document.body.appendChild(errorMsg);
      setTimeout(() => {
        document.body.removeChild(errorMsg);
      }, 3000);
      return;
    }

    if (appCoordinator) {
      const textToolService = appCoordinator.getTextToolService();
      if (textToolService) {
        textToolService.setVariable(newKey.trim(), newValue.trim());
        const updatedVariables = textToolService.getAllVariables();
        setVariables(updatedVariables);
        setNewKey('');
        setNewValue('');
      }
    }
  };

  // Modifier une variable existante
  const handleUpdateVariable = (key: string, value: string) => {
    if (appCoordinator) {
      const textToolService = appCoordinator.getTextToolService();
      if (textToolService) {
        textToolService.setVariable(key, value);
        const updatedVariables = textToolService.getAllVariables();
        setVariables(updatedVariables);
        setEditingKey(null);
      }
    }
  };

  // A18 : ouvre la confirmation au lieu de confirm().
  const handleDeleteVariable = (key: string) => {
    setDeleteKey(key);
  };

  const confirmDeleteVariable = () => {
    if (!deleteKey) return;
    if (appCoordinator) {
      const textToolService = appCoordinator.getTextToolService();
      if (textToolService) {
        textToolService.deleteVariable(deleteKey);
        const updatedVariables = textToolService.getAllVariables();
        setVariables(updatedVariables);
      }
    }
    setDeleteKey(null);
  };

  // Activer le mode édition
  const handleStartEdit = (key: string) => {
    setEditingKey(key);
  };

  // Annuler l'édition
  const handleCancelEdit = () => {
    setEditingKey(null);
  };

  const variableEntries = Object.entries(variables);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Gestionnaire de variables" size="medium">
      <div className="flex flex-col gap-6">
        {/* Instructions */}
        <Card padding="md" shadow="sm">
          <p className="mb-2 font-semibold text-sm">Comment utiliser les variables :</p>
          <ul className="m-0 pl-5 text-sm text-gray-600">
            <li>
              Utilisez <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">{'{{nom}}'}</code>{' '}
              dans vos textes
            </li>
            <li>Les variables seront remplacées automatiquement par leur valeur</li>
            <li>Exemple : "Bonjour {'{{prenom}}'}" devient "Bonjour Jean"</li>
          </ul>
        </Card>

        <Divider />

        {/* Liste des variables existantes */}
        <div>
          <h3 className="text-base font-semibold mb-3 text-gray-900">
            Variables existantes ({variableEntries.length})
          </h3>
          {variableEntries.length === 0 ? (
            <Card padding="lg" shadow="sm">
              <div className="text-center text-gray-500">Aucune variable définie</div>
            </Card>
          ) : (
            <div className="flex flex-col gap-2">
              {variableEntries.map(([key, value]) => (
                <Card key={key} padding="md" shadow="sm">
                  <div className="flex items-center gap-2">
                    <code className="px-2 py-1 bg-gray-100 rounded text-xs font-mono text-blue-600 font-semibold">
                      {'{{'}
                      {key}
                      {'}}'}
                    </code>
                    {editingKey === key ? (
                      <>
                        <Input
                          type="text"
                          defaultValue={value}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              handleUpdateVariable(key, (e.target as HTMLInputElement).value);
                            } else if (e.key === 'Escape') {
                              handleCancelEdit();
                            }
                          }}
                          onBlur={e => {
                            handleUpdateVariable(key, e.target.value);
                          }}
                          autoFocus
                          className="flex-1"
                        />
                        <Button variant="secondary" size="sm" onClick={() => handleCancelEdit()}>
                          Annuler
                        </Button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 text-gray-700">{value}</span>
                        <IconButton
                          variant="ghost"
                          size="sm"
                          onClick={() => handleStartEdit(key)}
                          aria-label="Modifier"
                          icon={<Edit className="w-4 h-4" />}
                        />
                        <IconButton
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeleteVariable(key)}
                          aria-label="Supprimer"
                          icon={<Trash2 className="w-4 h-4" />}
                        />
                      </>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        <Divider />

        {/* Formulaire d'ajout */}
        <div>
          <h3 className="text-base font-semibold mb-3 text-gray-900">Ajouter une variable</h3>
          <div className="flex flex-col gap-3">
            <Input
              type="text"
              label="Clé (nom de la variable)"
              value={newKey}
              onChange={e => setNewKey(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && newKey.trim() && newValue.trim()) {
                  handleAddVariable();
                }
              }}
              placeholder="prenom"
              helperText="Utilisez uniquement des lettres, chiffres et underscores"
              fullWidth
            />
            <Input
              type="text"
              label="Valeur"
              value={newValue}
              onChange={e => setNewValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && newKey.trim() && newValue.trim()) {
                  handleAddVariable();
                }
              }}
              placeholder="Jean"
              fullWidth
            />
            <Button
              variant="primary"
              size="md"
              onClick={handleAddVariable}
              disabled={!newKey.trim() || !newValue.trim()}
              leftIcon={<Plus className="w-4 h-4" />}
              fullWidth
            >
              Ajouter la variable
            </Button>
          </div>
        </div>
      </div>

      {/* A18 : confirmation de suppression de variable */}
      <ConfirmModal
        isOpen={!!deleteKey}
        danger
        title="Supprimer cette variable ?"
        message={
          <>
            La variable <strong>{`{{${deleteKey ?? ''}}}`}</strong> sera supprimée. Les textes qui
            l'utilisent afficheront le libellé brut.
          </>
        }
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        onConfirm={confirmDeleteVariable}
        onClose={() => setDeleteKey(null)}
      />
    </Modal>
  );
};
